import { useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { crosswordGridSize } from '../contentTypes'
import type { ChoiceContent, CrosswordContent, ListeningContent, TrueFalseContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'
import { ChoiceRenderer, TrueFalseRenderer } from './ChoiceRenderers'
import { VerdictMark } from './VerdictMark'
import { verdictFrameSx } from './verdictStyles'

/**
 * Рендереры медийных типов: сетка кроссворда/филворда и аудирование с
 * серверным лимитом прослушиваний.
 */

/**
 * Карта результатов из `check`: ключ элемента → его разбор.
 *
 * Сервер сохраняет карты с числовыми ключами то объектом, то списком (в JSON
 * `{"0": …}` неотличим от массива), поэтому читаем обе формы. Всё незнакомое
 * отбрасываем молча: экран без подсветки — это ровно сегодняшний экран, а
 * подсветка, собранная из догадок, врёт ученику про его же ответ.
 */
function checkEntries(value: unknown): Record<string, Record<string, unknown>> {
  const source = Array.isArray(value)
    ? Object.fromEntries(value.map((item, index) => [String(index), item]))
    : value

  if (typeof source !== 'object' || source === null) return {}

  const entries: Record<string, Record<string, unknown>> = {}
  Object.entries(source as Record<string, unknown>).forEach(([key, item]) => {
    if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
      entries[key] = item as Record<string, unknown>
    }
  })

  return entries
}

/**
 * Вердикт по элементу: `null` — вердикта нет.
 *
 * У аудирования разбор подвопроса бывает вложенным (со своими элементами
 * внутри) и без собственного `ok` — такой блок оставляем неокрашенным, красить
 * «в среднем» тут нечего.
 */
function okOf(entry: Record<string, unknown> | undefined): boolean | null {
  return typeof entry?.ok === 'boolean' ? entry.ok : null
}

/** Ответ кроссворда: `{ cells: { "row:col": "A" } }`; филворда: `{ found: [слова] }` */
export function CrosswordRenderer({ content, answers, onChange, readOnly }: RendererProps<CrosswordContent>) {
  const { t } = useTranslation()

  if (content.variant === 'wordsearch') {
    return <WordsearchRenderer content={content} answers={answers} onChange={onChange} readOnly={readOnly} />
  }

  const cells = typeof answers.cells === 'object' && answers.cells !== null
    ? (answers.cells as Record<string, string>)
    : {}

  const grid = content.grid
  // Размеры читаем через хелпер: сервер кладёт их и плоско, и в grid.size —
  // прямое чтение grid.cols давало repeat(undefined, 32px) и пустую сетку
  const { rows, cols } = crosswordGridSize(grid)
  const placements = grid?.placements ?? []

  if (!grid || rows === 0 || cols === 0) return <Alert severity="warning">{t('exercises.run.gridMissing')}</Alert>

  // Клетки сетки, доступные для ввода: только те, что заняты словами
  const editable = new Map<string, number>()
  placements.forEach((placement, placementIndex) => {
    placement.cells.forEach((cell) => editable.set(`${cell.row}:${cell.col}`, placementIndex))
  })

  // Номера начальных клеток: без них подсказка «3 по вертикали» не с чем
  // сопоставить — ученик пересчитывал слова глазами. У сеток, сохранённых до
  // появления нумерации, номеров нет: карта останется пустой, цифры не
  // отрисуются, сетка работает как раньше.
  const cellNumbers = new Map<string, number>()
  placements.forEach((placement) => {
    const first = placement.cells[0]
    const number = first?.number ?? placement.number
    if (first && number !== undefined) cellNumbers.set(`${first.row}:${first.col}`, number)
  })

  return (
    <Stack spacing={3}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 32px)`,
            gap: '2px',
            width: 'max-content',
          }}
        >
          {Array.from({ length: rows * cols }, (_, index) => {
            const row = Math.floor(index / cols)
            const col = index % cols
            const key = `${row}:${col}`
            const isEditable = editable.has(key)

            if (!isEditable) return <Box key={key} sx={{ width: 32, height: 32, bgcolor: 'action.hover' }} />

            const cellNumber = cellNumbers.get(key)

            return (
              <Box key={key} sx={{ position: 'relative', width: 32, height: 32 }}>
                {cellNumber !== undefined && (
                  <Typography
                    component="span"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 2,
                      // Мелко и без перехвата кликов: цифра не должна мешать
                      // попасть в клетку и вписать букву.
                      fontSize: 9,
                      lineHeight: 1.1,
                      color: 'text.secondary',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  >
                    {cellNumber}
                  </Typography>
                )}
                <Box
                  component="input"
                  maxLength={1}
                  value={cells[key] ?? ''}
                  disabled={readOnly}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({ ...answers, cells: { ...cells, [key]: event.target.value.toUpperCase().slice(0, 1) } })
                  }
                  sx={{
                    width: 32,
                    height: 32,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    font: 'inherit',
                    // Буква ниже цифры: иначе они наезжают друг на друга.
                    pt: cellNumber !== undefined ? '4px' : 0,
                  }}
                />
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Подсказки группами по направлению: так их читают в бумажном
          кроссворде, и номер сразу ищется в своём столбце. Порядок внутри
          группы задал сервер — он отдаёт размещения по возрастанию номера. */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.clues')}
        </Typography>
        <Stack spacing={2}>
          {(['across', 'down'] as const).map((direction) => {
            const group = placements
              .map((placement, index) => ({ placement, index }))
              .filter(({ placement }) => placement.direction === direction)

            if (group.length === 0) return null

            return (
              <Box key={direction}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t(direction === 'across' ? 'exercises.run.across' : 'exercises.run.down')}
                </Typography>
                <Stack spacing={0.5}>
                  {group.map(({ placement, index }) => (
                    <Typography key={index} variant="body2">
                      {placement.number ?? index + 1}. {placement.clue || content.words[index]?.clue || ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </Box>
    </Stack>
  )
}

function WordsearchRenderer({ content, answers, onChange, readOnly }: RendererProps<CrosswordContent>) {
  const { t } = useTranslation()
  const found = Array.isArray(answers.found) ? (answers.found as string[]) : []
  const [typed, setTyped] = useState('')
  const letters = content.grid?.letters ?? []

  // Без букв поле пустое: молчаливая пустота выглядела как «задание сломалось»
  if (letters.length === 0) return <Alert severity="warning">{t('exercises.run.gridMissing')}</Alert>

  return (
    <Stack spacing={3}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'inline-block', fontFamily: 'monospace', fontSize: 20, letterSpacing: 6 }}>
          {letters.map((row, rowIndex) => (
            <Box key={rowIndex}>{row.join(' ')}</Box>
          ))}
        </Box>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="input"
          value={typed}
          disabled={readOnly}
          placeholder={t('exercises.run.foundWord')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTyped(event.target.value.toUpperCase())}
          sx={{
            p: 1,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'transparent',
            color: 'text.primary',
            font: 'inherit',
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={readOnly || typed.trim().length < 2}
          onClick={() => {
            onChange({ ...answers, found: [...found, typed.trim()] })
            setTyped('')
          }}
        >
          {t('exercises.run.addFound')}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {found.map((word, index) => (
          <Chip
            key={`${word}-${index}`}
            label={word}
            disabled={readOnly}
            onDelete={readOnly ? undefined : () => onChange({ ...answers, found: found.filter((_, i) => i !== index) })}
          />
        ))}
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.clues')}
        </Typography>
        <Stack spacing={0.5}>
          {content.words.map((word, index) => (
            <Typography key={index} variant="body2">
              {index + 1}. {word.clue}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

/** Ответ аудирования: `{ questions: [ответ вложенного вопроса, …] }` */
export function ListeningRenderer({ content, answers, onChange, readOnly, check }: RendererProps<ListeningContent>) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [plays, setPlays] = useState(0)
  const maxPlays = content.max_plays ?? null
  const playsLeft = maxPlays === null ? null : Math.max(maxPlays - plays, 0)

  const questionAnswers = useMemo(
    () => (Array.isArray(answers.questions) ? (answers.questions as Array<Record<string, unknown>>) : []),
    [answers.questions],
  )

  // Разбор подвопросов: `{ questions: { "0": {ok: …}, … } }`, ключ — индекс
  // вопроса. Собираем его только для закрытой работы: решающему ученику сервер
  // разбор не отдаёт вовсе, и лишний расчёт на каждом кадре ему ни к чему
  const questionChecks = useMemo(
    () => (readOnly === true ? checkEntries(check?.questions) : {}),
    [readOnly, check],
  )

  const setQuestionAnswer = (index: number, value: Record<string, unknown>) => {
    const next = content.questions.map((_, i) => (i === index ? value : questionAnswers[i] ?? {}))
    onChange({ ...answers, questions: next })
  }

  const audioUrl = content.audio?.url ?? content.audio?.path ?? null

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={readOnly || audioUrl === null || playsLeft === 0}
          onClick={() => {
            setPlays((value) => value + 1)
            void audioRef.current?.play()
          }}
        >
          {t('exercises.run.play')}
        </Button>
        {playsLeft !== null && (
          <Typography variant="body2" color="text.secondary">
            {t('exercises.run.playsLeft', { count: playsLeft })}
          </Typography>
        )}
      </Stack>

      {audioUrl && <Box component="audio" ref={audioRef} src={audioUrl} sx={{ display: 'none' }} />}
      {audioUrl === null && <Alert severity="warning">{t('exercises.run.audioMissing')}</Alert>}

      <Divider />

      <Stack spacing={3}>
        {content.questions.map((question, index) => {
          const questionCheck = questionChecks[String(index)]
          const ok = okOf(questionCheck)

          return (
            <Box
              key={index}
              // Без вердикта блок остаётся ровно таким, каким был: рамка и
              // отступы появляются только вместе с подсветкой
              sx={
                ok === null
                  ? undefined
                  : (theme: Theme) => ({
                      p: 1.5,
                      borderRadius: 1,
                      // Рамка и подложка вердикта, текст основным цветом (EA-596)
                      ...verdictFrameSx(theme, ok ? 'correct' : 'wrong'),
                    })
              }
            >
              {ok !== null && (
                // Словом, а не только цветом: при дальтонизме зелёный и красный
                // блоки различаются лишь подписью
                <Typography
                  variant="caption"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontWeight: 600, color: 'text.primary' }}
                >
                  <VerdictMark outcome={ok ? 'correct' : 'wrong'} fontSize="inherit" />
                  {t(ok ? 'exercises.run.listeningCorrect' : 'exercises.run.listeningWrong')}
                </Typography>
              )}

              {question.type === 'choice' ? (
                <ChoiceRenderer
                  content={question.content as unknown as ChoiceContent}
                  answers={questionAnswers[index] ?? {}}
                  onChange={(value) => setQuestionAnswer(index, value)}
                  readOnly={readOnly}
                  // Разбор подвопроса уходит его же рендереру: у аудирования во
                  // вложенном ответе лежит тот же формат, что у отдельного
                  // задания, и разбирать его вторым разбором тут незачем
                  check={questionCheck ?? null}
                />
              ) : (
                <TrueFalseRenderer
                  content={question.content as unknown as TrueFalseContent}
                  answers={questionAnswers[index] ?? {}}
                  onChange={(value) => setQuestionAnswer(index, value)}
                  readOnly={readOnly}
                  check={questionCheck ?? null}
                />
              )}
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
